import {computed, makeObservable, observable, action} from 'mobx';
import {compact, every, flatten, isArray, isEmpty, map, reverse, uniq} from 'lodash';

import Step from './Step';

const historyLength = 100;

export type Change = Step[];
export interface IEntity {
  id: string;
}

class ChangesStore {
  rootStore: any;

  @observable changes: Change[] = [];
  @observable pointer: number = -1;

  @computed
  get length(): number {
    return this.changes.length;
  }

  @computed
  get isEmpty(): boolean {
    return this.length === 0;
  }

  @computed
  get canUndo(): boolean {
    return this.pointer >= 0;
  }

  @computed
  get canRedo(): boolean {
    return this.pointer < this.length - 1;
  }

  @computed
  get current(): Change | null {
    return this.isEmpty ? null : this.changes[this.pointer];
  }

  @computed
  get currentIds(): string[] {
    return compact(map(this.current, 'id'));
  }

  @action
  splice() {
    // Remove all steps after the pointer
    if (!isEmpty(this.changes.splice(this.pointer + 1))) {
      // If some steps were removed, the pointer must be updated
      this.changes = [...this.changes];
    }
  }

  @action
  rollBack() {
    if (this.canUndo) {
      this.undo();
      this.splice();
    }
  }

  currentEqualsTo(change: IEntity[], strict?: boolean): boolean {
    return !this.isEmpty &&
      every(change, ({id}) => this.currentIds.includes(id)) &&
      (!strict || change.length === this.current!.length);
  }

  @action
  register(change: Change, injectBefore?: boolean, checkDuplicates?: boolean) {
    // If user undoed some steps before registering a new one
    // existing redoes must be removed
    if (this.canRedo) {
      this.splice();
    }

    if (checkDuplicates && this.currentEqualsTo(change as IEntity[])) {
      // If change is already registered, avoid duplication
      return;
    }

    const newChange = flatten([change]);
    if (injectBefore && this.canUndo) {
      // The change can be inserted before the pointer
      this.changes.splice(this.pointer, 0, newChange);
    } else {
      // or on top of the stack
      this.changes.push(newChange);
    }
    // If history becomes longer allowed limit
    if (this.changes.length > historyLength) {
      // ... shift it (pointer mustn't change)
      this.changes.shift();
    } else {
      // increase the pointer otherwise
      this.pointer++;
    }
  }

  @action
  add(item) {
    this.register([Step.creation(item)]);
  }

  @action
  delete(item) {
    this.register([Step.deletion(item)]);
  }

  @action
  change(fromItem, toItem) {
    this.register([Step.modification(toItem, fromItem)]);
  }

  @action
  undo() {
    if (!this.canUndo) return false;
    this.pointer--;
    return reverse(map(this.changes[this.pointer + 1], 'inverted'));
  }

  @action
  redo() {
    if (!this.canRedo) return false;
    this.pointer++;
    return this.changes[this.pointer];
  }

  @action
  reset() {
    this.changes = [];
    this.pointer = -1;
  }

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.reset();
  }

  currentCorrespondsTo(id) {
    // Check if the current change corresponds to the given id (ids), so it could be reused
    const ids = isArray(id) ? uniq(id) : [id];
    return this.current?.length === ids.length &&
      every(this.current, ({id: stepId, isDelete}) => ids.includes(stepId) && !isDelete);
  }
}

export default ChangesStore;
